import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import ReportIssueForm from "@/components/ReportIssueForm";

const STATUS_LABEL = {
  OPEN: "Received", IN_PROGRESS: "In progress", WAITING_CUSTOMER: "Waiting for you", WAITING_VENDOR: "Checking with shop",
  ESCALATED: "In progress", RESOLVED: "Resolved", CLOSED: "Closed",
};

// G13: customers raise and follow help requests (support tickets). ALL-3: the
// form is the guided "Report an issue" (what about → problem → which order/booking → details + photos).
export default function Help({ embedded = false }) {
  const [sp] = useSearchParams();
  // ?order=<id> or ?booking=<id> (from an order or a care booking) opens the form part-filled.
  const preset = sp.get("order") ? { category: "order", item_id: sp.get("order") }
    : sp.get("booking") ? { category: "booking", item_id: sp.get("booking") } : {};
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/customer/tickets").then(({ data }) => setTickets(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const view = async (id) => {
    try { const { data } = await api.get(`/customer/tickets/${id}`); setOpen(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/customer/tickets/${open.id}/messages`, { body: reply });
      setReply("");
      await view(open.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className={embedded ? "" : "max-w-5xl mx-auto px-4 sm:px-6 py-8"}>
      <div className="mb-6">
        <h1 className={`font-heading font-semibold tracking-tight flex items-center gap-2 ${embedded ? "text-2xl" : "text-3xl sm:text-4xl"}`}><LifeBuoy className="h-7 w-7 text-marigold" /> How can we help?</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl border-stone-200">
          <ReportIssueForm preset={preset} onSent={async (t) => { await load(); await view(t.id); }} />
        </Card>

        <div className="space-y-3">
          {open ? (
            <Card className="p-5 rounded-2xl border-stone-200" data-testid="help-ticket">
              <button type="button" onClick={() => setOpen(null)} className="text-xs text-stone-500 hover:underline">← All requests</button>
              <div className="flex items-center justify-between mt-2">
                <div className="font-semibold">{open.subject}</div>
                <Badge className="bg-stone-100 text-stone-700 border-0 rounded-full">{STATUS_LABEL[open.status] || open.status}</Badge>
              </div>
              <div className="text-xs text-stone-500">{open.ticket_number}</div>
              <div className="mt-3 text-sm whitespace-pre-wrap bg-stone-50 rounded-xl p-3">{open.description}</div>
              <div className="mt-3 space-y-2">
                {open.messages.map((m, i) => (
                  <div key={i} className={`text-sm rounded-xl p-3 ${m.actor_role === "customer" ? "bg-teal/5 ml-6" : "bg-marigold/10 mr-6"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{m.actor_role === "customer" ? "You" : "PawBazaar support"} · {new Date(m.created_at).toLocaleString()}</div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              {!["RESOLVED", "CLOSED"].includes(open.status) && (
                <div className="mt-3 flex gap-2">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="h-10" data-testid="help-reply" />
                  <Button type="button" disabled={busy || !reply.trim()} onClick={sendReply} className="rounded-full h-10">Send</Button>
                </div>
              )}
            </Card>
          ) : tickets.length === 0 ? (
            <Card className="p-6 text-sm text-stone-600 rounded-2xl border-dashed">You haven't raised any help requests.</Card>
          ) : tickets.map(t => (
            <Card key={t.id} onClick={() => view(t.id)} className="p-4 rounded-2xl border-stone-200 cursor-pointer hover:border-stone-300" data-testid={`help-ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm truncate">{t.subject}</div>
                <Badge className="bg-stone-100 text-stone-700 border-0 rounded-full text-[10px] shrink-0">{STATUS_LABEL[t.status] || t.status}</Badge>
              </div>
              <div className="text-xs text-stone-500 mt-1">{t.ticket_number} · {new Date(t.created_at).toLocaleDateString()}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
